import React, { useCallback, useEffect, useRef, useState } from "react";
import { WhatsNewExperience } from "./WhatsNewExperience";
import {
  WHATS_NEW_OPEN_EVENT,
  WHATS_NEW_RELEASE,
  isAutoLaunchEligible,
  isAutoLaunchSuppressed,
  setAutoLaunchSuppressed,
  setSessionDismissed,
} from "./whatsNewStore";

/**
 * Opens What's New automatically for an authenticated, non-guest user when
 * this release is still eligible: not durably suppressed ("Don't show again")
 * and not already dismissed in this browser session. Manual opening (the
 * Settings event) always works and ignores both guards. A reload remounts
 * this host and re-evaluates eligibility; session dismissal survives that
 * reload, durable suppression survives a new session.
 */
export function WhatsNewHost({ actor, authLoading = false, release = WHATS_NEW_RELEASE }) {
  const isAuthenticated = Boolean(actor) && !actor.isGuest && !authLoading;
  const autoPresentedRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [dontShow, setDontShow] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      // Signed out / still loading: never show. Re-arm so the next
      // authenticated user is evaluated against that user's own guards.
      autoPresentedRef.current = false;
      setOpen(false);
      return;
    }
    if (!autoPresentedRef.current) {
      autoPresentedRef.current = true;
      const suppressed = isAutoLaunchSuppressed(actor, release);
      setDontShow(suppressed);
      if (isAutoLaunchEligible(actor, release)) setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, release]);

  useEffect(() => {
    const handleOpen = () => {
      // Manual open: always allowed. Show the current preference in the control.
      setDontShow(isAutoLaunchSuppressed(actor, release));
      setOpen(true);
    };
    window.addEventListener(WHATS_NEW_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(WHATS_NEW_OPEN_EVENT, handleOpen);
  }, [actor, release]);

  const handleClose = useCallback(() => {
    setSessionDismissed(actor, release);
    setOpen(false);
  }, [actor, release]);
  const handleDontShow = useCallback(
    (next) => {
      setDontShow(next);
      setAutoLaunchSuppressed(actor, next, release);
    },
    [actor, release],
  );

  return open ? (
    <WhatsNewExperience
      onClose={handleClose}
      dontShowAgain={dontShow}
      onDontShowAgainChange={handleDontShow}
    />
  ) : null;
}
